import { createBrowserRouter, Navigate, RouteObject } from "react-router-dom";

import { ErrorPage } from "@/common/components/error-page/error-page";
import { Features } from "@/common/providers/feature-provider";
import { PlanSchema } from "@/common/services/user";
import {
  FeatureMode,
  getFeatureMode,
} from "@/domain/plan/feature-mode.service";
import { Nullable } from "@/types";

import { Anonymous } from "./anonymous/anonymous";
import { BlockedUser } from "./blocked-user/blocked-user";
import { Callback, path as callbackPath } from "./callback/callback";
import * as IncompleteUser from "./incomplete-user/incomplete-user";
import { Layout } from "./layout/layout";
import { Logout } from "./logout/logout";
import * as PurchasePrices from "./purchase-prices/purchase-prices";
import { path as quickTradingPath } from "./quick-trading/quick-trading";
import { Root } from "./root/root";
import { routes as adminRoutes } from "./routes.admin";
import {
  getReportsRoutes,
  marketAiRoutes,
  stockExchangeRoutes,
  yieldPredictionsRoutes,
} from "./routes.basic";
import { getMarketplaceRoutes } from "./routes.marketplace";
import {
  getPurchasePricesRoutes,
  quickTradingRoutes,
  storagesRoutes,
  tradingRoutes,
  transactionsAndOrdersRoutes,
} from "./routes.trading";
import { NavigateToInitialPage } from "./routes.utils";
import { LockedScreen } from "./shared/components/locked-screen/locked-screen";
import { path as stockExchangePath } from "./stock-exchange/stock-exchange";
import { Layout as SubscriptionLayout } from "./subscriptions/layout";
import { Subscribed } from "./subscriptions/subscribed/subscribed";
import { Unsubscribed } from "./subscriptions/unsubscribed/unsubscribed";
import { SwitchCompany } from "./switch-company/switch-company";

type RouteConfig = {
  path: string;
  routes: Record<FeatureMode, RouteObject[]>;
  feature: keyof Features;
};

const createIncompleteUserRouter = () =>
  createBrowserRouter([
    {
      element: <IncompleteUser.IncompleteUser />,
      path: IncompleteUser.path,
    },
    {
      element: <Logout />,
      path: "/logout",
    },
    {
      element: <Navigate to={"/" + IncompleteUser.path} />,
      path: "*",
    },
  ]);

const createBlockedUserRouter = () =>
  createBrowserRouter([
    {
      element: <BlockedUser />,
      path: "*",
    },
    {
      element: <Logout />,
      path: "/logout",
    },
  ]);

const createAnonymousRouter = () =>
  createBrowserRouter([
    {
      element: <Anonymous />,
      path: "*",
    },
    {
      element: <Callback />,
      path: callbackPath,
    },
  ]);

// TODO import paths from subroutes
const featureRoutes: RouteConfig[] = [
  {
    feature: "MARKETPLACE",
    path: "marketplace",
    routes: {
      full: getMarketplaceRoutes("full"),
      "not-available": getMarketplaceRoutes("trial"),
      trial: getMarketplaceRoutes("trial"),
    },
  },
  {
    feature: "TRADING",
    path: "trades",
    routes: {
      full: tradingRoutes,
      "not-available": [],
      trial: [
        {
          element: <LockedScreen />,
          path: "trades/*",
        },
      ],
    },
  },
  {
    feature: "QUICK_TRADING",
    path: "quick-trading",
    routes: {
      full: quickTradingRoutes,
      "not-available": [],
      trial: [{ element: <LockedScreen />, path: "quick-trading/*" }],
    },
  },
  {
    feature: "PURCHASE_PRICES",
    path: "purchase-prices",
    routes: {
      full: getPurchasePricesRoutes("full"),
      "not-available": [],
      trial: getPurchasePricesRoutes("trial"),
    },
  },
  {
    feature: "STORAGES",
    path: "storages",
    routes: {
      full: storagesRoutes,
      "not-available": [],
      trial: [{ element: <LockedScreen />, path: "storages/*" }],
    },
  },
  {
    feature: "TRANSACTIONS_ORDERS",
    path: "transactions-and-orders",
    routes: {
      full: transactionsAndOrdersRoutes,
      "not-available": [],
      trial: [{ element: <LockedScreen />, path: "transactions-and-orders/*" }],
    },
  },
  {
    feature: "STOCK_EXCHANGE",
    path: "stock-exchange",
    routes: {
      full: stockExchangeRoutes,
      "not-available": [],
      trial: [{ element: <LockedScreen />, path: "stock-exchange/*" }],
    },
  },
  {
    feature: "MARKET_NEWS",
    path: "reports",
    routes: {
      full: getReportsRoutes("full"),
      "not-available": [],
      trial: getReportsRoutes("trial"),
    },
  },
  {
    feature: "YIELD_PREDICTIONS",
    path: "yield-predictions",
    routes: {
      full: yieldPredictionsRoutes,
      "not-available": [],
      trial: [{ element: <LockedScreen />, path: "yield-predictions/*" }],
    },
  },
];

const getFeatureRoutes = (
  featureConfig: RouteConfig,
  companyPlan: Nullable<PlanSchema["planId"]>,
  features: Features,
): RouteObject[] => {
  const mode = getFeatureMode(companyPlan, features[featureConfig.feature]);
  return featureConfig.routes[mode];
};

const getRoutes = (
  companyPlan: Nullable<PlanSchema["planId"]>,
  features: Features,
) => [
  {
    element: <Logout />,
    path: "/logout",
  },
  {
    element: <SwitchCompany />,
    path: "/switch-company",
  },
  {
    element: (
      <Root>
        <SubscriptionLayout>
          <Subscribed />
        </SubscriptionLayout>
      </Root>
    ),
    path: "/subscribed",
  },
  {
    element: (
      <Root>
        <SubscriptionLayout>
          <Unsubscribed />
        </SubscriptionLayout>
      </Root>
    ),
    path: "/unsubscribed",
  },
  {
    children: [
      {
        element: <Callback />,
        path: callbackPath,
      },
      {
        children: [
          {
            element: <LockedScreen />,
            path: "pricing",
          },
          ...marketAiRoutes,
          ...featureRoutes.flatMap((config) =>
            getFeatureRoutes(config, companyPlan, features),
          ),
        ],
        element: <Layout />,
      },
    ],
    element: (
      <Root>
        <NavigateToInitialPage
          page={resolveInitialPage(companyPlan, features)}
        />
      </Root>
    ),
    errorElement: <ErrorPage />,
    path: "/",
  },
  // just to be sure user initially marked as incomplete never lands on incomplete path
  // when eventually marked as complete
  {
    element: <Navigate replace to="/" />,
    path: IncompleteUser.path,
  },
  ...adminRoutes(),
];

const resolveInitialPage = (
  companyPlan: Nullable<PlanSchema["planId"]>,
  features: Features,
) => {
  if (features.QUICK_TRADING?.planId === companyPlan) {
    return quickTradingPath;
  }

  if (features.PURCHASE_PRICES?.planId === companyPlan) {
    return PurchasePrices.path;
  }

  if (features.PURCHASE_PRICES?.mode === "TRIAL") {
    return PurchasePrices.path;
  }

  return stockExchangePath;
};

const createRouter = (
  companyPlan: Nullable<PlanSchema["planId"]>,
  features: Features,
) => createBrowserRouter(getRoutes(companyPlan, features));

export {
  createAnonymousRouter,
  createBlockedUserRouter,
  createIncompleteUserRouter,
  createRouter,
  getRoutes,
};
